// src/components/Timeline/components/TimelineEvent.tsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Avatar,
  TextField,
  Stack,
  Chip,
  Collapse,
  Badge,
  useTheme,
  useMediaQuery,
  Tooltip,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Image as ImageIcon,
  Send as SendIcon,
  Lock as LockIcon,
  People as PeopleIcon,
  Place as PlaceIcon,
  AccessTime as TimeIcon,
  Hotel as HotelIcon,
  Flight as FlightIcon,
  DirectionsCar as TransportIcon,
  LocalActivity as ActivityIcon,
  Restaurant as FoodIcon,
  Comment as CommentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { TimelineEvent, TripCollaborator, Comment as CommentType } from '../types';

interface TimelineEventCardProps {
  event: TimelineEvent;
  isPastTrip: boolean;
  currentUser?: {
    id: string;
    name: string;
    avatar?: string;
  };
  collaborators: TripCollaborator[];
  onAddComment?: (eventId: string, comment: string) => void;
  onOpenImageDialog: (images: string[], startIndex?: number) => void;
  onShowCollaborators: () => void;
  onEditEvent?: (event: TimelineEvent) => void;
  onDeleteEvent?: (eventId: string) => void;
  onLikeEvent?: (eventId: string, liked: boolean) => void;
}

const TimelineEventCard: React.FC<TimelineEventCardProps> = ({
  event,
  isPastTrip,
  currentUser,
  collaborators,
  onAddComment,
  onOpenImageDialog,
  onShowCollaborators,
  onEditEvent,
  onDeleteEvent,
  onLikeEvent
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const hasImages = event.images && event.images.length > 0;
  const hasComments = event.comments && event.comments.length > 0;
  const canEdit = !isPastTrip && onEditEvent && currentUser; 
  const canDelete = !isPastTrip && onDeleteEvent && currentUser;
  
  // Get the appropriate icon based on event type
  const getEventTypeIcon = () => {
    switch (event.type) {
      case 'accommodation':
        return <HotelIcon fontSize="small" color="success" />;
      case 'transportation':
        const transportType = event.reference && 'type' in event.reference ? event.reference.type : null;
        return transportType === 'flight' ? 
          <FlightIcon fontSize="small" color="warning" /> : 
          <TransportIcon fontSize="small" color="warning" />;
      case 'activity':
        if (event.reference && 'tags' in event.reference) {
          const tags = event.reference.tags || [];
          if (tags.includes('Food') || tags.includes('Restaurant')) {
            return <FoodIcon fontSize="small" color="primary" />;
          }
        }
        return <ActivityIcon fontSize="small" color="primary" />;
      default:
        return <ActivityIcon fontSize="small" color="primary" />;
    }
  };
  
  const handleCommentSubmit = () => {
    if (onAddComment && commentText.trim()) {
      onAddComment(event.id, commentText);
      setCommentText('');
    }
  };
  
  const handleLikeToggle = () => {
    if (onLikeEvent) {
      const newLikedState = !isLiked;
      setIsLiked(newLikedState);
      onLikeEvent(event.id, newLikedState);
    }
  };
  
  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        mb: 2, 
        borderLeft: '4px solid',
        borderColor: event.type === 'activity' ? 'primary.main' : 
                    event.type === 'accommodation' ? 'success.main' :
                    event.type === 'transportation' ? 'warning.main' : 'info.main',
        position: 'relative',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 4
        }
      }}
    >
      {/* Trip sharing indicator */}
      {collaborators.length > 0 && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 8, 
            right: 8, 
            zIndex: 1 
          }}
        >
          <Tooltip title={`Shared with ${collaborators.length} people`}>
            <IconButton 
              size="small" 
              onClick={onShowCollaborators}
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.8)', 
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' } 
              }}
            >
              <Badge badgeContent={collaborators.length} color="primary">
                <PeopleIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>
      )}
      
      {/* Event header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              width: 32, 
              height: 32, 
              borderRadius: '50%', 
              bgcolor: 'background.default' 
            }}
          >
            {getEventTypeIcon()}
          </Box>
          <Box sx={{ flex: 1, pr: { xs: 6, sm: 8 } }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {event.title}
            </Typography>
            
            {event.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {event.description}
              </Typography>
            )}
            
            {/* Event metadata */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                flexWrap: 'wrap', 
                gap: 1.5, 
                mt: 1
              }}
            >
              {/* Location info if available */}
              {event.reference && 'location' in event.reference && event.reference.location && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PlaceIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                  <Typography variant="caption" color="text.secondary">
                    {event.reference.location}
                  </Typography>
                </Box>
              )}
              
              {/* Time info if available */}
              {event.reference && 'startTime' in event.reference && event.reference.startTime && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TimeIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                  <Typography variant="caption" color="text.secondary">
                    {event.reference.startTime}
                    {event.reference.endTime && ` - ${event.reference.endTime}`}
                  </Typography>
                </Box>
              )}
              
              {/* Comment count if there are comments */}
              {hasComments && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CommentIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                  <Typography variant="caption" color="text.secondary">
                    {event.comments?.length}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        
        <Stack direction="row" spacing={0.5}>
          {/* Action buttons */}
          {canEdit && (
            <Tooltip title="Edit">
              <IconButton 
                size="small" 
                onClick={() => onEditEvent && onEditEvent(event)}
                sx={{ 
                  bgcolor: 'background.default',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {canDelete && (
            <Tooltip title="Delete">
              <IconButton 
                size="small" 
                onClick={() => onDeleteEvent && onDeleteEvent(event.id)}
                sx={{ 
                  bgcolor: 'background.default',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {hasImages && (
            <Tooltip title="View photos">
              <IconButton 
                size="small" 
                onClick={() => onOpenImageDialog(event.images || [])}
                sx={{ 
                  bgcolor: 'background.default',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Badge badgeContent={event.images?.length} color="primary">
                  <ImageIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          
          {onLikeEvent && (
            <Tooltip title={isLiked ? "Unlike" : "Like"}>
              <IconButton 
                size="small" 
                onClick={handleLikeToggle}
                sx={{ 
                  bgcolor: 'background.default',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                {isLiked ? (
                  <FavoriteIcon fontSize="small" color="error" />
                ) : (
                  <FavoriteBorderIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
          
          <Tooltip title={isExpanded ? "Collapse" : "Expand"}>
            <IconButton 
              size="small" 
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{ 
                transform: isExpanded ? 'rotate(180deg)' : 'none', 
                transition: 'transform 0.2s',
                bgcolor: 'background.default',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      
      {/* Image Thumbnails */}
      {hasImages && (
        <Box 
          sx={{ 
            mt: 2, 
            display: 'flex', 
            overflowX: 'auto', 
            pb: 1,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.05)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: 4,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.3)',
              },
            },
          }}
        >
          {event.images?.slice(0, 6).map((img, index) => (
            <Box 
              key={index}
              sx={{ 
                width: { xs: 70, sm: 80 }, 
                height: { xs: 70, sm: 80 }, 
                flexShrink: 0,
                mr: 1, 
                backgroundImage: `url(${img})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: 1,
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
              onClick={() => onOpenImageDialog(event.images || [], index)}
            />
          ))}
          {(event.images?.length || 0) > 6 && (
            <Box 
              sx={{ 
                width: { xs: 70, sm: 80 }, 
                height: { xs: 70, sm: 80 }, 
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.15)'
                }
              }}
              onClick={() => onOpenImageDialog(event.images || [])}
            >
              <Typography variant="body2" fontWeight="bold">
                +{(event.images?.length || 0) - 6} more
              </Typography>
            </Box>
          )}
        </Box>
      )}
      
      {/* Expandable content */}
      <Collapse in={isExpanded}>
        <Box sx={{ mt: 2 }}>
          {/* Tags if available */}
          {event.reference && 'tags' in event.reference && event.reference.tags && event.reference.tags.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {event.reference.tags.map((tag: string, index: number) => (
                  <Chip 
                    key={index} 
                    label={tag} 
                    size="small" 
                    variant="outlined"
                    sx={{ borderRadius: 1 }}
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {/* Comments section */}
          {(hasComments || onAddComment) && (
            <Box sx={{ mt: 2 }}>
              {hasComments && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <CommentIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Comments ({event.comments?.length})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ mb: 2 }}>
                    {event.comments?.map((comment) => (
                      <CommentItem key={comment.id} comment={comment} />
                    ))}
                  </Box>
                </>
              )}
              
              {/* Comment input - only for non-past trips */}
              {!isPastTrip && onAddComment && currentUser && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Avatar 
                    src={currentUser.avatar} 
                    sx={{ width: 32, height: 32, mr: 1.5 }}
                  >
                    {currentUser.name.charAt(0)}
                  </Avatar>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add a comment..."
                    variant="outlined"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleCommentSubmit();
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <IconButton 
                          edge="end" 
                          onClick={handleCommentSubmit}
                          disabled={!commentText.trim()}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      ),
                    }}
                  />
                </Box>
              )}
              
              {/* Lock indicator for past trips */}
              {isPastTrip && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <LockIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    Comments are locked for past trips
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// Comment Item Component
interface CommentItemProps {
  comment: CommentType;
}

const CommentItem: React.FC<CommentItemProps> = ({ comment }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      mb: 2,
      alignItems: 'flex-start'
    }}
  >
    <Avatar 
      src={comment.user.avatar} 
      sx={{ width: 32, height: 32, mr: 1.5 }}
    >
      {comment.user.name.charAt(0)}
    </Avatar>
    <Box sx={{ flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ mr: 1 }}>
          {comment.user.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {format(new Date(comment.timestamp), 'MMM d, h:mm a')}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {comment.text}
      </Typography>
    </Box>
  </Box>
);

export default TimelineEventCard;